import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import PrioritizeDetailModal, { PrioritizeItem } from '../../components/PrioritizeDetailModal';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useActiveProject } from '../../hooks/useActiveProject';
import { useFeatureLinkData } from '../../hooks/useFeatureLinkData';
import BoardControlsButton, { BoardPrefs, loadBoardPrefs, isColumnVisible } from '../../components/BoardControlsButton';
import { organizationService, OrgMember } from '../../services/organizationService';
import { prioritizeService } from '../../services/prioritizeService';
import '../../styles/Pages.css';
import '../../styles/CustomersList.css';
import '../../styles/Prioritize.css';
import '../../styles/PrioritizeModal.css';

/* ── Types ──────────────────────────────────────────────────── */
export type ItemKind = 'product' | 'epic' | 'feature' | 'initiative' | 'release' | 'objective' | 'release-group';
export type HealthState = 'on-track' | 'at-risk' | 'off-track' | 'none';
export type FeatureStatus = 'new-idea' | 'candidate' | 'planned' | 'in-progress' | 'released' | 'backlog' | 'done' | 'upcoming' | 'completed';

/* ── Column definitions (reorderable, resizable) ──────────── */
type ColKey = 'name' | 'owner' | 'score' | 'timeframe' | 'status' | 'health' | 'progress' | 'effort' | 'tag' | 'hierarchy';

interface ColDef {
  key: ColKey;
  label: string;
  defaultWidth: number;
  minWidth: number;
  fixed?: boolean;
  appliesTo?: (kind: ItemKind) => boolean;
}

const COL_DEFS: ColDef[] = [
  { key: 'name',      label: 'Products, Components, Features', defaultWidth: 320, minWidth: 220, fixed: true },
  { key: 'owner',     label: 'Owner',     defaultWidth: 110, minWidth: 70 },
  { key: 'score',     label: 'Customer Importance Score', defaultWidth: 100, minWidth: 70 },
  { key: 'timeframe', label: 'Timeframe', defaultWidth: 160, minWidth: 90, appliesTo: k => k === 'feature' || k === 'epic' },
  { key: 'status',    label: 'Status',    defaultWidth: 110, minWidth: 80, appliesTo: k => k === 'feature' },
  { key: 'health',    label: 'Health',    defaultWidth: 130, minWidth: 80, appliesTo: k => k === 'feature' },
  { key: 'progress',  label: '% Work progress', defaultWidth: 140, minWidth: 80, appliesTo: k => k === 'feature' },
  { key: 'effort',    label: 'Effort',    defaultWidth: 90,  minWidth: 60, appliesTo: k => k === 'feature' },
  { key: 'tag',       label: 'Sample Tag', defaultWidth: 110, minWidth: 70, appliesTo: k => k === 'feature' },
  { key: 'hierarchy', label: 'Hierarchy parent', defaultWidth: 180, minWidth: 100, appliesTo: k => k !== 'product' },
];

/* ── Constants & Status/Health styles ──────────────────────── */
const STATUS_PILL_STYLE: Record<FeatureStatus, { bg: string; fg: string; label: string }> = {
  'in-progress': { bg: '#ECEAFF', fg: '#432EA3', label: 'In progress' },
  'new-idea':    { bg: '#ccfbf1', fg: '#115e59', label: 'New idea' },
  'candidate':   { bg: '#f3e8ff', fg: '#6b21a8', label: 'Candidate' },
  'planned':     { bg: '#dbeafe', fg: '#1e40af', label: 'Planned' },
  'released':    { bg: '#d1fae5', fg: '#047857', label: 'Released' },
  'backlog':     { bg: '#f3f4f6', fg: '#4b5563', label: 'Backlog' },
  'done':        { bg: '#d1fae5', fg: '#047857', label: 'Done' },
  'upcoming':    { bg: '#dbeafe', fg: '#1e40af', label: 'Upcoming' },
  'completed':   { bg: '#d1fae5', fg: '#047857', label: 'Completed' },
};
const HEALTH_STYLE: Record<HealthState, { color: string; label: string; ago: string }> = {
  'on-track':  { color: '#10B981', label: 'On track',  ago: '3 days ago' },
  'at-risk':   { color: '#F97316', label: 'At risk',   ago: '3 days ago' },
  'off-track': { color: '#EF4444', label: 'Off track', ago: '3 days ago' },
  'none':      { color: '#9ca3af', label: '—',         ago: '' },
};
// Health updates store an icon name ('up' | 'wave' | 'down'); map it to a HealthState.
const HEALTH_ICON_TO_STATE: Record<string, HealthState> = {
  up: 'on-track', wave: 'at-risk', down: 'off-track',
};

/* ── Static symbols (UI-only icons) ───────────────────────── */
/* Product = box/drawer outline, Component = 2×2 grid outline,
   Feature = small filled square (kept as a solid accent). */
const ProductSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = '#64748b' }) => (
  <span className="prioritize-symbol-icon" title="Product" style={{ color }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
    </svg>
  </span>
);
const ComponentSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = '#64748b' }) => (
  <span className="prioritize-symbol-icon" title="Component" style={{ color }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  </span>
);
const FeatureSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'var(--brand-500)' }) => (
  <span className="prioritize-symbol-feature" style={{ width: size, height: size, background: color }} title="Feature" />
);

/* ── Field icons (small inline SVGs) ──────────────────────── */
const FIcon: React.FC<{ name: string; size?: number; color?: string }> = ({ name, size = 12, color = '#6b7280' }) => {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'owner':      return <svg {...props}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>;
    case 'score':      return <svg {...props}><path d="M9 21h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/></svg>;
    case 'timeframe':  return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    case 'status':     return <svg {...props}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>;
    case 'health':     return <svg {...props}><path d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0 4 3 6 0"/></svg>;
    case 'progress':   return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case 'plus':       return <svg {...props}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
    case 'chevron':    return <svg {...props}><polyline points="9 18 15 12 9 6" /></svg>;
    case 'dot':        return <svg {...props}><circle cx="12" cy="12" r="3" fill={color} stroke="none" /></svg>;
    case 'effort':     return <svg {...props}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case 'tag':        return <svg {...props}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
    case 'hierarchy':  return <svg {...props}><path d="M3 6h6M9 6v6h6M15 12v6M3 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM15 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 12a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM15 18a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/></svg>;
    case 'search':     return <svg {...props}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
    default: return null;
  }
};

const Avatar: React.FC<{ name?: string | null; size?: number; color?: string }> = ({ name, size = 22, color = '#EC4899' }) => {
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  return <span className="prm-avatar" style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.42) }}>{initials}</span>;
};

const NotAssigned: React.FC = () => <span className="prm-not-assigned">Not assigned</span>;

/* ── Helpers ───────────────────────────────────────────────── */
const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
const formatDateRange = (start?: string, end?: string): string => {
  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const s = fmt(start), e = fmt(end);
  if (s && e) return `${s} – ${e}`;
  return s || e;
};

/* ── Main component ─────────────────────────────────────────── */
const Prioritize: React.FC = () => {
  const { user, currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('product_prioritize');
  const { activeProjectId } = useActiveProject();
  const projectKey = activeProjectId ?? 'all';
  const { companies, customerUsers, documents } = useFeatureLinkData(currentOrg?.id, activeProjectId);

  const [items, setItems] = useState<PrioritizeItem[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PrioritizeItem | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openPlusFor, setOpenPlusFor] = useState<string | null>(null);
  const [inlineCreate, setInlineCreate] = useState<{ parentId: string; kind: ItemKind } | null>(null);
  const [inlineName, setInlineName] = useState('');
  const inlineInputRef = useRef<HTMLInputElement>(null);

  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: 'before' | 'after' | 'inside' } | null>(null);

  /* Plus-menu anchor (button DOM rect) — used so the dropdown escapes
     the `overflow: auto` table wrapper and renders above other rows.   */
  const [plusAnchor, setPlusAnchor] = useState<{ top: number; left: number; } | null>(null);

  /* Column order & widths */
  const [colOrder, setColOrder] = useState<ColKey[]>(() => {
    try { return JSON.parse(localStorage.getItem('prm:colOrder') || 'null') || COL_DEFS.map(c => c.key); }
    catch { return COL_DEFS.map(c => c.key); }
  });
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('prm:colWidths') || 'null');
      const merged: Record<string, number> = {};
      COL_DEFS.forEach(c => merged[c.key] = c.defaultWidth);
      if (raw) Object.keys(raw).forEach(k => { merged[k] = raw[k]; });
      return merged as Record<ColKey, number>;
    } catch {
      const merged: Record<string, number> = {};
      COL_DEFS.forEach(c => merged[c.key] = c.defaultWidth);
      return merged as Record<ColKey, number>;
    }
  });
  useEffect(() => { localStorage.setItem('prm:colOrder', JSON.stringify(colOrder)); }, [colOrder]);
  useEffect(() => { localStorage.setItem('prm:colWidths', JSON.stringify(colWidths)); }, [colWidths]);

  /* Board controls — which columns are visible (default: all). */
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs('prioritize:boardControls'));
  const visibleColKeys = colOrder.filter(k => k !== 'name' && isColumnVisible(boardPrefs.columns, k));

  /* Expand all products and epics by default */
  useEffect(() => {
    setExpanded(prev => {
      if (prev.size > 0) return prev;
      const next = new Set<string>();
      items.forEach(i => {
        if (i.kind === 'product' || i.kind === 'epic') next.add(i.id);
      });
      return next;
    });
  }, [items.length === 0]);

  /* Load items from Supabase (scoped to org via RLS + active project). */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentOrg?.id) return;
      try {
        const rows = await prioritizeService.getItems(activeProjectId);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error('Failed to load prioritize items:', err);
        if (!cancelled) setItems([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentOrg?.id, activeProjectId]);

  /* Load members */
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        if (!currentOrg?.id) return;
        const result = await organizationService.getMembersWithProfiles(currentOrg.id);
        setMembers(result);
      } catch (error) {
        console.error('Failed to load members:', error);
      }
    };
    fetchMembers();
  }, [currentOrg?.id]);

  /* Close plus menu on outside click */
  useEffect(() => {
    if (!openPlusFor) return;
    const h = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.prm-plus-wrap') || t.closest('.prm-plus-menu') || t.closest('.prm-plus-menu-fixed')) return;
      setOpenPlusFor(null);
      setPlusAnchor(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [openPlusFor]);

  useEffect(() => {
    if (inlineCreate) {
      setTimeout(() => inlineInputRef.current?.focus(), 30);
    }
  }, [inlineCreate]);

  /* ── Filters & helpers ──────────────────────────────── */
  const q = search.trim().toLowerCase();
  const match = (s: string) => !q || s.toLowerCase().includes(q);
  const filtered = useMemo(() => items.filter(i => match(i.name)), [items, q]);

  const memberById = (id?: string | null) => id ? members.find(m => m.user_id === id) ?? null : null;
  const itemById = (id: string) => items.find(i => i.id === id);

  const toggleExpand = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const isExpanded = (id: string) => expanded.has(id);

  const childIds = (id: string): string[] => {
    const item = itemById(id);
    if (!item) return [];
    if (item.kind === 'product') {
      return filtered.filter(i =>
        (i.kind === 'epic'    && i.parentId === id) ||
        (i.kind === 'feature' && i.productParentId === id)
      ).map(i => i.id);
    }
    if (item.kind === 'epic') {
      return filtered.filter(i => i.kind === 'feature' && (i.parentId === id || i.productParentId === id)).map(i => i.id);
    }
    return [];
  };

  const isParentExpanded = (id: string): boolean => {
    const it = itemById(id);
    if (!it) return true;
    if (it.kind === 'product') return true;
    if (it.kind === 'epic') return it.parentId ? isExpanded(it.parentId) : true;
    const parentId = it.parentId || it.productParentId;
    return parentId ? isExpanded(parentId) : true;
  };

  /* ── Item handlers ───────────────────────────────────── */
  const handleItemClick = (item: PrioritizeItem) => {
    setSelectedId(item.id);
    setDraft(item);
  };

  /* Compute parentCandidates for the hierarchy picker */
  const parentCandidates = useMemo(() => {
    if (!draft) return items;
    if (draft.kind === 'product') return items.filter(i => i.id !== draft.id && i.kind === 'product');
    if (draft.kind === 'epic') return items.filter(i => i.id !== draft.id && i.kind === 'product');
    return items.filter(i => {
      if (i.id === draft.id) return false;
      return i.kind === 'product' || i.kind === 'epic';
    });
  }, [draft, items]);

  /* Collect an item id + all of its descendant ids (components & features). */
  const collectSubtreeIds = (id: string, all: PrioritizeItem[]): string[] => {
    const item = all.find(i => i.id === id);
    if (!item) return [id];
    const ids = [id];
    if (item.kind === 'product') {
      const epics = all.filter(i => i.kind === 'epic' && i.parentId === id).map(i => i.id);
      epics.forEach(eid => ids.push(...collectSubtreeIds(eid, all)));
      all.filter(i => i.kind === 'feature' && i.productParentId === id).forEach(i => ids.push(i.id));
    } else if (item.kind === 'epic') {
      all.filter(i => i.kind === 'feature' && (i.parentId === id || i.productParentId === id)).forEach(i => ids.push(i.id));
    }
    return Array.from(new Set(ids));
  };

  const handleDelete = (id: string) => {
    const item = itemById(id);
    if (!item) return;
    if (!window.confirm('Delete this item? Features and components under it will also be removed.')) return;
    const ids = collectSubtreeIds(id, items);
    const idSet = new Set(ids);
    setItems(prev => prev.filter(i => !idSet.has(i.id)));
    setSelectedId(null);
    prioritizeService.deleteItems(ids).catch(err => {
      console.error('Failed to delete item(s):', err);
      alert('Failed to delete. Please refresh and try again.');
    });
  };

  // Delete invoked from the detail modal (which already shows its own confirm),
  // so skip the extra window.confirm here. Removes the item + its subtree.
  const handleDeleteFromModal = (id: string) => {
    const ids = collectSubtreeIds(id, items);
    const idSet = new Set(ids);
    setItems(prev => prev.filter(i => !idSet.has(i.id)));
    setSelectedId(null);
    setDraft(null);
    prioritizeService.deleteItems(ids).catch(err => {
      console.error('Failed to delete item(s):', err);
      alert('Failed to delete. Please refresh and try again.');
    });
  };

  const handleSave = (updated: PrioritizeItem) => {
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setDraft(null);
    prioritizeService.updateItem(updated.id, updated).catch(err => {
      console.error('Failed to save item:', err);
      alert(`Failed to save changes: ${err?.message ?? err}`);
    });
  };

  const handleCancel = () => setDraft(null);

  /* ── Add helpers ────────────────────────────────────── */
  const startInlineCreate = (parentId: string, kind: ItemKind) => {
    setOpenPlusFor(null);
    setPlusAnchor(null);
    setInlineCreate({ parentId, kind });
    setInlineName('');
  };
  const cancelInline = () => { setInlineCreate(null); setInlineName(''); setPlusAnchor(null); };
  const submitInline = async () => {
    if (!inlineCreate) return;
    const name = inlineName.trim();
    if (!name) return;
    const { parentId, kind } = inlineCreate;
    const newItem: PrioritizeItem = {
      id: 'temp_' + Math.random().toString(36).slice(2, 10),
      kind,
      name,
      ownerId: null,
      importanceScore: 0,
      color: kind === 'product' ? '#3B82F6' : kind === 'epic' ? '#8B5CF6' : 'var(--brand-500)',
      health: 'none',
      status: 'backlog',
      workProgress: 0,
      parentId: kind === 'epic' ? parentId : null,
      productParentId: kind === 'feature' ? parentId : null,
      createdAt: new Date().toISOString(),
    };
    setInlineCreate(null);
    setInlineName('');
    setExpanded(prev => new Set(prev).add(parentId));
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      handleItemClick(saved);
    } catch (err) {
      console.error('Failed to create item:', err);
      alert('Failed to create item. Please try again.');
    }
  };

  /* ── Drag-and-drop handlers ─────────────────────────── */
  const canDropOn = (dragged: PrioritizeItem, target: PrioritizeItem): { ok: boolean; position: 'before' | 'after' | 'inside' } => {
    if (dragged.id === target.id) return { ok: false, position: 'before' };
    if (dragged.kind === 'product') {
      if (target.kind === 'product') return { ok: true, position: 'before' };
      return { ok: false, position: 'before' };
    }
    if (dragged.kind === 'epic') {
      if (target.kind === 'product') return { ok: true, position: 'inside' };
      return { ok: false, position: 'before' };
    }
    if (dragged.kind === 'feature') {
      if (target.kind === 'product') return { ok: true, position: 'inside' };
      if (target.kind === 'epic') return { ok: true, position: 'inside' };
      return { ok: false, position: 'before' };
    }
    return { ok: false, position: 'before' };
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent, id: string) => {
    if (!dragId) return;
    const dragged = itemById(dragId);
    const target = itemById(id);
    if (!dragged || !target) return;
    const check = canDropOn(dragged, target);
    if (!check.ok) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ id, position: check.position });
  };
  const handleDragEnd = () => {
    setDragId(null);
    setDropTarget(null);
  };
  /* Persist new ordering + parent links after a drag-drop. position is the
     item's index in the flat array; cheap and good enough for sibling order. */
  const persistOrder = (arr: PrioritizeItem[]) => {
    prioritizeService.updatePositions(
      arr.map((i, idx) => ({
        id: i.id,
        position: idx,
        parentId: i.parentId ?? null,
        productParentId: i.productParentId ?? null,
      })),
    ).catch(err => console.error('Failed to persist order:', err));
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!dragId) return;
    const dragged = itemById(dragId);
    const target = itemById(targetId);
    if (!dragged || !target) return;
    const check = canDropOn(dragged, target);
    if (!check.ok) return;

    if (check.position === 'inside') {
      // 1. Re-parent the dragged item.
      const reparented = items.map(i => {
        if (i.id === dragged.id) {
          if (dragged.kind === 'epic') return { ...i, parentId: target.id };
          if (dragged.kind === 'feature') {
            if (target.kind === 'product') return { ...i, productParentId: target.id, parentId: null };
            if (target.kind === 'epic') return { ...i, parentId: target.id, productParentId: null };
          }
        }
        return i;
      });

      // 2. Reposition it in the array so it renders directly under the
      //    target's existing children — otherwise the row keeps its old
      //    index and "slides" far down the list.
      const moved = reparented.find(i => i.id === dragged.id)!;
      const without = reparented.filter(i => i.id !== dragged.id);

      // Find the insert point: after the target and all of its current
      // descendants (so the new child lands at the bottom of that group).
      const targetIdx = without.findIndex(i => i.id === target.id);
      let insertAt = targetIdx + 1;
      for (let j = targetIdx + 1; j < without.length; j++) {
        const it = without[j];
        const isDescendant =
          it.parentId === target.id ||
          it.productParentId === target.id ||
          // grandchildren: feature under a component that belongs to target
          (target.kind === 'product' && it.kind === 'feature' &&
            without.some(c => c.id === it.parentId && c.parentId === target.id));
        if (isDescendant) insertAt = j + 1;
        else break;
      }
      without.splice(insertAt, 0, moved);

      setItems(without);
      persistOrder(without);
      setExpanded(prev => new Set(prev).add(target.id));
    } else {
      const parentId = dragged.kind === 'product' ? null : (dragged.kind === 'epic' ? dragged.parentId : (dragged.parentId || dragged.productParentId));
      const siblings = items
        .filter(i => {
          if (dragged.kind === 'product') return i.kind === 'product';
          if (dragged.kind === 'epic') return i.kind === 'epic' && i.parentId === parentId;
          return (i.kind === 'feature') && ((i.parentId && i.parentId === parentId) || (i.productParentId && i.productParentId === parentId));
        })
        .map(i => i.id)
        .filter(id => id !== dragged.id);
      const targetIdx = siblings.indexOf(targetId);
      if (targetIdx < 0) return;
      const insertIdx = check.position === 'before' ? targetIdx : targetIdx + 1;
      siblings.splice(insertIdx, 0, dragged.id);
      const newOrder: PrioritizeItem[] = [];
      const siblingSet = new Set(siblings);
      let sibIdx = 0;
      for (const i of items) {
        if (siblingSet.has(i.id)) {
          newOrder.push(items.find(x => x.id === siblings[sibIdx++])!);
        } else {
          newOrder.push(i);
        }
      }
      setItems(newOrder);
      persistOrder(newOrder);
    }
    setDragId(null);
    setDropTarget(null);
  };

  /* Column resize */
  const resizingRef = useRef<{ key: ColKey; startX: number; startW: number } | null>(null);
  const onResizeStart = (e: React.MouseEvent, key: ColKey) => {
    e.preventDefault();
    resizingRef.current = { key, startX: e.clientX, startW: colWidths[key] };
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const dx = ev.clientX - resizingRef.current.startX;
      const def = COL_DEFS.find(c => c.key === resizingRef.current!.key)!;
      const next = Math.max(def.minWidth, resizingRef.current.startW + dx);
      setColWidths(w => ({ ...w, [resizingRef.current!.key]: next }));
    };
    const onUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  /* Column reorder */
  const colDragRef = useRef<{ key: ColKey; startX: number } | null>(null);
  const onColDragStart = (e: React.DragEvent, key: ColKey) => {
    colDragRef.current = { key, startX: e.clientX };
    e.dataTransfer.effectAllowed = 'move';
    const img = new Image();
    img.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"></svg>';
    e.dataTransfer.setDragImage(img, 0, 0);
  };
  const onColDragOver = (e: React.DragEvent, key: ColKey) => {
    if (!colDragRef.current) return;
    e.preventDefault();
  };
  const onColDrop = (e: React.DragEvent, key: ColKey) => {
    if (!colDragRef.current) return;
    e.preventDefault();
    const fromKey = colDragRef.current.key;
    colDragRef.current = null;
    if (fromKey === key) return;
    setColOrder(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(key);
      if (fromIdx < 0 || toIdx < 0) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      return next;
    });
  };

  /* ── Render helpers ────────────────────────────────── */
  const renderSymbol = (item: PrioritizeItem) => {
    // Product/component use neutral outline icons; feature keeps its accent fill.
    if (item.kind === 'product') return <ProductSymbol size={16} />;
    if (item.kind === 'epic') return <ComponentSymbol size={16} />;
    return <FeatureSymbol size={16} color={item.color || 'var(--brand-500)'} />;
  };

  const renderCell = (item: PrioritizeItem, col: ColKey): React.ReactNode => {
    switch (col) {
      case 'name':      return null;
      case 'owner': {
        const m = memberById(item.ownerId);
        return m ? <span className="prm-list-owner"><Avatar name={m.full_name || m.email} size={20} /> <span>{m.full_name || m.email}</span></span> : <NotAssigned />;
      }
      case 'score': {
        // Sum of importance points of this feature's insights (0 if none).
        const total = (item.insights ?? []).reduce((sum, ins) => sum + (ins.importance ?? 0), 0);
        return total > 0 ? <strong>{total}</strong> : <span className="prm-zero">0</span>;
      }
      case 'timeframe': {
        const s = formatDateRange(item.startDate, item.endDate);
        return s ? <span style={{ color: '#111827', fontSize: 12 }}>{s}</span> : <NotAssigned />;
      }
      case 'status': {
        if (item.kind !== 'feature') return <NotAssigned />;
        if (!item.status) return <NotAssigned />;
        const c = STATUS_PILL_STYLE[item.status];
        return (
          <span className="prm-list-pill" style={{ background: c.bg, color: c.fg }}>
            <span className="prm-list-pill-bar" style={{ background: c.fg }} />
            {c.label}
          </span>
        );
      }
      case 'health': {
        if (item.kind !== 'feature') return <NotAssigned />;
        // Prefer the latest posted health update; fall back to the scalar field.
        const updates = item.healthUpdates ?? [];
        const latest = updates.length ? updates[updates.length - 1] : null;
        const state: HealthState = latest
          ? (HEALTH_ICON_TO_STATE[latest.status] ?? 'none')
          : (item.health || 'none');
        if (state === 'none') return <NotAssigned />;
        const h = HEALTH_STYLE[state];
        return (
          <span className="prm-list-health">
            <span className="prm-list-health-icon" style={{ background: h.color }}>
              <FIcon name="health" size={9} color="white" />
            </span>
            <span>{h.label}</span>
            <span className="prm-list-health-ago">{latest?.at ?? h.ago}</span>
          </span>
        );
      }
      case 'progress': {
        if (item.kind !== 'feature') return <NotAssigned />;
        const pct = item.workProgress ?? 0;
        return (
          <span className="prm-list-progress">
            <span className="prm-list-progress-track">
              <span className="prm-list-progress-fill" style={{ width: `${pct}%` }} />
            </span>
            <span className="prm-list-progress-num">{pct}%</span>
          </span>
        );
      }
      case 'effort':    return item.effort != null ? <span><FIcon name="effort" size={11} color="#6b7280" /> <strong>{item.effort}</strong></span> : <NotAssigned />;
      case 'tag': {
        const tags = (item.sampleTag ?? '').split(',').map(t => t.trim()).filter(Boolean);
        if (tags.length === 0) return <NotAssigned />;
        return (
          <span className="prm-list-tags">
            {tags.map((t, i) => <span key={i} className="prm-list-tag-chip">{t}</span>)}
          </span>
        );
      }
      case 'hierarchy': {
        if (item.kind === 'product') return <NotAssigned />;
        const parentId = item.parentId || item.productParentId;
        if (!parentId) return <NotAssigned />;
        const p = itemById(parentId);
        if (!p) return <NotAssigned />;
        return (
          <span className="prm-list-hierarchy">
            {p.kind === 'product' ? <ProductSymbol size={13} /> : <ComponentSymbol size={13} />}
            <span>{p.name}</span>
          </span>
        );
      }
    }
  };

  const renderRow = (item: PrioritizeItem, depth: number): React.ReactNode => {
    if (!isParentExpanded(item.id)) return null;

    const children = childIds(item.id);
    const canExpand = children.length > 0;
    const isOpen = isExpanded(item.id);
    const isDragging = dragId === item.id;
    const dropPos = (dropTarget && dropTarget.id === item.id) ? dropTarget.position : null;
    const indent = depth * 20;
    const owner = memberById(item.ownerId);
    const isProduct = item.kind === 'product';
    const isEpic = item.kind === 'epic';
    const isFeature = item.kind === 'feature';

    const plusOptions: Array<{ kind: ItemKind; label: string; symbol: React.ReactNode }> = [];
    if (isProduct) {
      plusOptions.push({ kind: 'epic', label: 'Component', symbol: <ComponentSymbol size={11} /> });
      plusOptions.push({ kind: 'feature', label: 'Feature', symbol: <FeatureSymbol size={11} /> });
    } else if (isEpic) {
      plusOptions.push({ kind: 'feature', label: 'Feature', symbol: <FeatureSymbol size={11} /> });
    }

    return (
      <React.Fragment key={item.id}>
        <tr
          className={`prm-row ${dragId === item.id ? 'prm-row-dragging' : ''} prm-row-drop-${dropPos ?? 'none'}`}
          draggable
          onDragStart={e => handleDragStart(e, item.id)}
          onDragOver={e => handleDragOver(e, item.id)}
          onDrop={e => handleDrop(e, item.id)}
          onDragEnd={handleDragEnd}
          onClick={() => handleItemClick(item)}
        >
          <td className="prm-cell prm-cell-name" style={{ width: colWidths.name, minWidth: colWidths.name }}>
            <div className="prm-cell-name-inner" style={{ paddingLeft: 8 + indent }}>
              <button className={`prm-expand-btn ${isOpen ? 'prm-expand-btn-open' : ''}`} onClick={(e) => { e.stopPropagation(); if (canExpand) toggleExpand(item.id); }} style={{ visibility: canExpand ? 'visible' : 'hidden' }}>
                <FIcon name="chevron" size={11} />
              </button>
              {renderSymbol(item)}
              <span className="prm-row-name">{item.name}</span>
              {canManage && plusOptions.length > 0 && (
                <span className="prm-plus-wrap">
                  <button
                    className="prm-plus-btn"
                    onClick={e => {
                      e.stopPropagation();
                      if (openPlusFor === item.id) {
                        setOpenPlusFor(null);
                        setPlusAnchor(null);
                      } else {
                        const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                        setPlusAnchor({ top: r.bottom + 4, left: r.left });
                        setOpenPlusFor(item.id);
                      }
                    }}
                  >
                    <FIcon name="plus" size={11} />
                  </button>
                </span>
              )}
            </div>
          </td>
          {visibleColKeys.map(key => {
            const def = COL_DEFS.find(c => c.key === key)!;
            if (def.appliesTo && !def.appliesTo(item.kind)) {
              return <td key={key} className="prm-cell prm-cell-na" style={{ width: colWidths[key], minWidth: colWidths[key] }}><NotAssigned /></td>;
            }
            return <td key={key} className="prm-cell" style={{ width: colWidths[key], minWidth: colWidths[key] }} onClick={() => handleItemClick(item)}>{renderCell(item, key)}</td>;
          })}
        </tr>
        {inlineCreate && inlineCreate.parentId === item.id && (
          <tr className="prm-row prm-row-inline-create" onClick={e => e.stopPropagation()}>
            <td className="prm-cell prm-cell-name" style={{ width: colWidths.name, minWidth: colWidths.name }}>
              <div className="prm-cell-name-inner" style={{ paddingLeft: 8 + indent + 32 }}>
                <span className="prm-inline-create-icon">
                  {inlineCreate.kind === 'epic' ? <ComponentSymbol size={14} /> : <FeatureSymbol size={14} />}
                </span>
                <input
                  ref={inlineInputRef}
                  className="prm-inline-create-input"
                  placeholder={`Enter ${inlineCreate.kind} name…`}
                  value={inlineName}
                  onChange={e => setInlineName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') submitInline();
                    if (e.key === 'Escape') cancelInline();
                  }}
                  onBlur={() => {
                    if (inlineName.trim()) submitInline();
                    else cancelInline();
                  }}
                />
              </div>
            </td>
            {visibleColKeys.map(key => <td key={key} className="prm-cell prm-cell-na" style={{ width: colWidths[key], minWidth: colWidths[key] }} />)}
          </tr>
        )}
        {isOpen && children.map(cid => renderRow(itemById(cid)!, depth + 1))}
      </React.Fragment>
    );
  };

  const topLevel = filtered.filter(i => i.kind === 'product');

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <h1 className="customers-title">Prioritize</h1>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search products, components, features…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
            <BoardControlsButton
              columns={COL_DEFS.filter(c => c.key !== 'name').map(c => ({ key: c.key, label: c.label }))}
              storageKey="prioritize:boardControls"
              prefs={boardPrefs}
              onChange={setBoardPrefs}
            />
            {canManage && (
              <button
                className="customers-add-btn btn-primary-cta"
                onClick={async () => {
                  const newItem: PrioritizeItem = { id: 'temp_' + uid(), kind: 'product', name: 'New Product', ownerId: null, importanceScore: 0, color: '#3B82F6', createdAt: new Date().toISOString() };
                  try {
                    const saved = await prioritizeService.createItem(newItem, items.length);
                    setItems(prev => [...prev, saved]);
                    handleItemClick(saved);
                  } catch (err) {
                    console.error('Failed to create product:', err);
                    alert('Failed to create product. Please try again.');
                  }
                }}
                title="Add a new product"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Product
              </button>
            )}
          </div>
        </div>

        <div className="prm-list-wrap">
          <div className="prm-list-table-wrap">
            <table className="prm-list-table">
              <colgroup>
                <col style={{ width: colWidths.name, minWidth: colWidths.name }} />
                {visibleColKeys.map(k => (
                  <col key={k} style={{ width: colWidths[k], minWidth: colWidths[k] }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="prm-th prm-th-fixed" style={{ width: colWidths.name, minWidth: colWidths.name }} draggable onDragStart={e => onColDragStart(e, 'name')} onDragOver={e => onColDragOver(e, 'name')} onDrop={e => onColDrop(e, 'name')}>
                    <span className="prm-th-label">Products, Components, Features</span>
                    <span className="prm-th-resize" draggable={false} onMouseDown={e => onResizeStart(e, 'name')} />
                  </th>
                  {visibleColKeys.map(key => {
                    const def = COL_DEFS.find(c => c.key === key)!;
                    return (
                      <th key={key} className="prm-th" style={{ width: colWidths[key], minWidth: colWidths[key] }} draggable onDragStart={e => onColDragStart(e, key)} onDragOver={e => onColDragOver(e, key)} onDrop={e => onColDrop(e, key)}>
                        <span className="prm-th-label">{def.label}</span>
                        <span className="prm-th-resize" draggable={false} onMouseDown={e => onResizeStart(e, key)} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {topLevel.length === 0 && !inlineCreate && (
                  <tr><td colSpan={visibleColKeys.length + 1} className="prm-empty">No items yet</td></tr>
                )}
                {topLevel.map(p => renderRow(p, 0))}
              </tbody>
            </table>
            {/* Plus-menu dropdown — rendered outside the table so the
                `overflow: auto` on the wrapper does not clip it.   */}
            {openPlusFor && plusAnchor && (() => {
              const item = items.find(i => i.id === openPlusFor);
              if (!item) return null;
              const plusOptions: Array<{ kind: ItemKind; label: string; symbol: React.ReactNode }> = [];
              if (item.kind === 'product') {
                plusOptions.push({ kind: 'epic',    label: 'Component', symbol: <ComponentSymbol size={11} /> });
                plusOptions.push({ kind: 'feature', label: 'Feature',   symbol: <FeatureSymbol size={11} /> });
              } else if (item.kind === 'epic') {
                plusOptions.push({ kind: 'feature', label: 'Feature',   symbol: <FeatureSymbol size={11} /> });
              }
              if (plusOptions.length === 0) return null;
              return (
                <div
                  className="prm-plus-menu-fixed"
                  style={{ top: plusAnchor.top, left: plusAnchor.left }}
                  onClick={e => e.stopPropagation()}
                >
                  {plusOptions.map(opt => (
                    <button
                      key={opt.kind}
                      className="prm-plus-menu-item"
                      onClick={() => {
                        startInlineCreate(item.id, opt.kind);
                        setPlusAnchor(null);
                      }}
                    >
                      {opt.symbol}
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {selectedId && draft && (
          <PrioritizeDetailModal
            item={draft}
            parentCandidates={parentCandidates}
            members={members}
            allItems={items}
            companies={companies}
            customerUsers={customerUsers}
            documents={documents}
            onDelete={handleDeleteFromModal}
            onClose={handleCancel}
            onSave={handleSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default Prioritize;